import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSettingsStore } from '@/store'
import { useHabitStore } from '@/store'
import { useFinanceStore } from '@/store'
import { useTasksStore } from '@/store'
import { useWeeklyStore } from '@/store'
import { timezones } from '@/lib/timezoneUtils'

export default function Settings() {
  const { t, i18n } = useTranslation()
  const {
    theme,
    toggleTheme,
    setLanguage,
    language,
    calendar,
    setCalendar,
    plannerName,
    setPlannerName,
    clockLocations,
    addClockLocation,
    removeClockLocation,
    weatherLocations,
    addWeatherLocation,
    removeWeatherLocation,
  } = useSettingsStore()
  
  const { habits, clearAllHabits } = useHabitStore()
  const { items, clearAllItems } = useFinanceStore()
  const { tasks, clearAllTasks } = useTasksStore()
  const { weeks, clearAllWeeks } = useWeeklyStore()

  const [newPlannerName, setNewPlannerName] = useState(plannerName)
  const [newClockCity, setNewClockCity] = useState('')
  const [newClockTimezone, setNewClockTimezone] = useState('Asia/Tehran')
  const [newWeatherCity, setNewWeatherCity] = useState('')

  const handleLanguageChange = (lang: 'fa' | 'en') => {
    setLanguage(lang)
    i18n.changeLanguage(lang)
    localStorage.setItem('language', lang)
    document.documentElement.lang = lang
    document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr'
  }

  const handleAddClock = () => {
    if (newClockCity.trim()) {
      addClockLocation({
        id: `clock_${Date.now()}`,
        city: newClockCity,
        timezone: newClockTimezone,
      })
      setNewClockCity('')
      setNewClockTimezone('Asia/Tehran')
    }
  }

  const handleAddWeather = () => {
    if (newWeatherCity.trim()) {
      // Comprehensive list of cities with their coordinates
      const cityCoordinates: Record<string, { lat: number; lon: number }> = {
        // Iranian cities
        'تهران': { lat: 35.6892, lon: 51.389 },
        'مشهد': { lat: 36.2605, lon: 59.5832 },
        'اصفهان': { lat: 32.6546, lon: 51.6243 },
        'تبریز': { lat: 38.0808, lon: 46.2919 },
        'کرج': { lat: 35.8364, lon: 51.0099 },
        'شیراز': { lat: 29.5832, lon: 52.5836 },
        'قم': { lat: 34.6413, lon: 50.8759 },
        'کیش': { lat: 26.5337, lon: 53.9776 },
        'بندر عباس': { lat: 27.1865, lon: 56.2808 },
        'رشت': { lat: 37.2808, lon: 49.5832 },
        'اهواز': { lat: 31.3183, lon: 48.6706 },
        'مریوان': { lat: 35.5211, lon: 46.1883 },
        'قزوین': { lat: 36.2663, lon: 50.0049 },
        'ارومیه': { lat: 37.5527, lon: 45.0755 },
        'یاسوج': { lat: 30.6657, lon: 51.5898 },
        'خرم‌آباد': { lat: 33.4861, lon: 48.3533 },
        'بیرجند': { lat: 32.8666, lon: 59.2277 },
        'سنندج': { lat: 35.3163, lon: 47.0018 },
        'بروجرد': { lat: 33.9007, lon: 48.7517 },
        'بناب': { lat: 37.3656, lon: 46.6123 },
        'ساری': { lat: 36.5648, lon: 53.0602 },
        'کیاسر': { lat: 36.7256, lon: 53.2667 },
        
        // European cities
        'Vienna': { lat: 48.2082, lon: 16.3738 },
        'Berlin': { lat: 52.5200, lon: 13.4050 },
        'Paris': { lat: 48.8566, lon: 2.3522 },
        'London': { lat: 51.5074, lon: -0.1278 },
        'Amsterdam': { lat: 52.3676, lon: 4.9041 },
        'Brussels': { lat: 50.8503, lon: 4.3517 },
        'Rome': { lat: 41.9028, lon: 12.4964 },
        'Milan': { lat: 45.4642, lon: 9.1900 },
        'Venice': { lat: 45.4408, lon: 12.3155 },
        'Florence': { lat: 43.7696, lon: 11.2558 },
        'Barcelona': { lat: 41.3851, lon: 2.1734 },
        'Madrid': { lat: 40.4168, lon: -3.7038 },
        'Lisbon': { lat: 38.7223, lon: -9.1393 },
        'Prague': { lat: 50.0755, lon: 14.4378 },
        'Budapest': { lat: 47.4979, lon: 19.0402 },
        'Athens': { lat: 37.9838, lon: 23.7275 },
        'Istanbul': { lat: 41.0082, lon: 28.9784 },
        'Warsaw': { lat: 52.2297, lon: 21.0122 },
        'Stockholm': { lat: 59.3293, lon: 18.0686 },
        'Copenhagen': { lat: 55.6761, lon: 12.5683 },
        'Oslo': { lat: 59.9139, lon: 10.7522 },
        'Moscow': { lat: 55.7558, lon: 37.6173 },
        'Dubai': { lat: 25.2048, lon: 55.2708 },
        'Tokyo': { lat: 35.6762, lon: 139.6503 },
        'New York': { lat: 40.7128, lon: -74.0060 },
        'Sydney': { lat: -33.8688, lon: 151.2093 },
        'Bangkok': { lat: 13.7563, lon: 100.5018 },
        'Singapore': { lat: 1.3521, lon: 103.8198 },
        'Hong Kong': { lat: 22.3193, lon: 114.1694 },
      }

      const coords = cityCoordinates[newWeatherCity] || { lat: 0, lon: 0 }

      addWeatherLocation({
        id: `weather_${Date.now()}`,
        city: newWeatherCity,
        latitude: coords.lat,
        longitude: coords.lon,
      })
      setNewWeatherCity('')
    }
  }

  return (
    <div className="h-full flex flex-col fade-in">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-4xl font-bold text-gray-800 dark:text-white">
          ⚙️ {t('sidebar.settings')}
        </h1>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto scrollbar-custom px-6 py-6">
        <div className="space-y-8">
          {/* Settings Grid - 4 columns for settings sections */}
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {/* Planner Name */}
            <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            📝 نام برنامه‌ریز
          </h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={newPlannerName}
              onChange={(e) => setNewPlannerName(e.target.value)}
              placeholder="Moji Planner"
              className="flex-1 px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => setPlannerName(newPlannerName || 'Moji Planner')}
              className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition font-semibold"
            >
              ذخیره
            </button>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            {t('settings.theme')}
          </h2>
          <button
            onClick={toggleTheme}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition"
          >
            {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
          </button>
        </div>

        {/* Language */}
        <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            {t('settings.language')}
          </h2>
          <div className="flex gap-4">
            <button
              onClick={() => handleLanguageChange('fa')}
              className={`px-4 py-2 rounded-lg transition ${
                language === 'fa'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 dark:bg-slate-700 hover:bg-gray-300'
              }`}
            >
              فارسی
            </button>
            <button
              onClick={() => handleLanguageChange('en')}
              className={`px-4 py-2 rounded-lg transition ${
                language === 'en'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 dark:bg-slate-700 hover:bg-gray-300'
              }`}
            >
              English
            </button>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            {t('settings.calendar')}
          </h2>
          <div className="flex gap-4">
            <button
              onClick={() => setCalendar('jalali')}
              className={`px-4 py-2 rounded-lg transition ${
                calendar === 'jalali'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 dark:bg-slate-700 hover:bg-gray-300'
              }`}
            >
              جلالی
            </button>
            <button
              onClick={() => setCalendar('gregorian')}
              className={`px-4 py-2 rounded-lg transition ${
                calendar === 'gregorian'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 dark:bg-slate-700 hover:bg-gray-300'
              }`}
            >
              میلادی
            </button>
          </div>
        </div>

        {/* Clock Locations */}
        <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            🕐 ساعت جهانی
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="City name (e.g., Dubai, Tokyo)"
                value={newClockCity}
                onChange={(e) => setNewClockCity(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:text-white dark:border-slate-600"
              />
              <select
                value={newClockTimezone}
                onChange={(e) => setNewClockTimezone(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:text-white dark:border-slate-600"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddClock}
                className="w-full px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition"
              >
                Add Clock
              </button>
            </div>

            {clockLocations.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                  Active Clocks:
                </p>
                {clockLocations.map((clock) => (
                  <div
                    key={clock.id}
                    className="flex justify-between items-center bg-gray-100 dark:bg-slate-700 p-3 rounded-lg"
                  >
                    <span className="text-gray-800 dark:text-white">
                      {clock.city} ({clock.timezone})
                    </span>
                    <button
                      onClick={() => removeClockLocation(clock.id)}
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Weather Locations */}
        <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            🌤️ هواشناسی
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <select
                value={newWeatherCity}
                onChange={(e) => setNewWeatherCity(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:text-white dark:border-slate-600"
              >
                <option value="">Select a city...</option>
                <optgroup label="🇮🇷 Iranian Cities">
                  {['تهران', 'مشهد', 'اصفهان', 'تبریز', 'کرج', 'شیراز', 'قم', 'کیش', 'بندر عباس', 'رشت', 'اهواز', 'مریوان', 'قزوین', 'ارومیه', 'یاسوج', 'خرم‌آباد', 'بیرجند', 'سنندج', 'بروجرد', 'بناب', 'ساری', 'کیاسر'].map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="🇪🇺 European Cities">
                  {['Vienna', 'Berlin', 'Paris', 'London', 'Amsterdam', 'Brussels', 'Rome', 'Milan', 'Venice', 'Florence', 'Barcelona', 'Madrid', 'Lisbon', 'Prague', 'Budapest', 'Athens', 'Istanbul', 'Warsaw', 'Stockholm', 'Copenhagen', 'Oslo'].map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="🌍 Other Cities">
                  {['Dubai', 'Tokyo', 'New York', 'Sydney', 'Bangkok', 'Singapore', 'Hong Kong', 'Moscow'].map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </optgroup>
              </select>
              <button
                onClick={handleAddWeather}
                className="w-full px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition"
              >
                Add Weather
              </button>
            </div>

            {weatherLocations.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                  Weather Monitoring:
                </p>
                {weatherLocations.map((weather) => (
                  <div
                    key={weather.id}
                    className="flex justify-between items-center bg-gray-100 dark:bg-slate-700 p-3 rounded-lg"
                  >
                    <span className="text-gray-800 dark:text-white">
                      {weather.city}
                    </span>
                    <button
                      onClick={() => removeWeatherLocation(weather.id)}
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Backup/Restore Card - Standalone */}
        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-md border-l-4 border-indigo-500 hover:shadow-lg transition overflow-hidden flex flex-col">
          <div className="flex-1 space-y-2">
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-lg">💾</span>
                {language === 'fa' ? 'بکاپ و بازیابی' : 'Backup & Restore'}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-tight">
                {language === 'fa' 
                  ? 'صادرات و واردات تمام داده‌های برنامه‌ریز (عادت‌ها، وظایف، برنامه‌ها و مالی)'
                  : 'Export and import all planner data (habits, tasks, schedules, and finances)'
                }
              </p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => {
                const allData = {
                  habits: habits || [],
                  tasks: tasks || [],
                  weeks: weeks || [],
                  items: items || [],
                  settings: {
                    theme,
                    language,
                    calendar,
                    plannerName,
                    clockLocations,
                    weatherLocations,
                  },
                  exportDate: new Date().toISOString(),
                }
                const dataStr = JSON.stringify(allData, null, 2)
                const dataBlob = new Blob([dataStr], { type: 'application/json' })
                const url = URL.createObjectURL(dataBlob)
                const link = document.createElement('a')
                link.href = url
                link.download = `moji-planner-backup-${new Date().getTime()}.json`
                link.click()
                URL.revokeObjectURL(url)
              }}
              className="flex-1 px-2 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold rounded transition"
            >
              📥 {language === 'fa' ? 'صادرات' : 'Export'}
            </button>
            <button
              onClick={() => {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = '.json'
                input.onchange = (e: any) => {
                  const file = e.target.files[0]
                  if (file) {
                    const reader = new FileReader()
                    reader.onload = (event: any) => {
                      try {
                        const data = JSON.parse(event.target.result)
                        alert(language === 'fa' 
                          ? 'فایل صادر شده برای بازیابی آماده است. لطفا تمام داده‌ها را دستی وارد کنید.'
                          : 'Backup file loaded. Please manually restore the data.'
                        )
                      } catch (error) {
                        alert(language === 'fa' 
                          ? 'خرابی در خواندن فایل'
                          : 'Error reading file'
                        )
                      }
                    }
                    reader.readAsText(file)
                  }
                }
                input.click()
              }}
              className="flex-1 px-2 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded transition"
            >
              📤 {language === 'fa' ? 'واردات' : 'Import'}
            </button>
          </div>
        </div>

        {/* Clear Data Cards Grid - 2 columns layout */}
        <div className="grid grid-cols-2 gap-3">
          {/* Habits Card */}
          <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-md border-l-4 border-purple-500 hover:shadow-lg transition overflow-hidden h-full flex flex-col">
            <div className="flex-1 space-y-1">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="text-lg">✅</span>
                  {t('settings.clearHabitsShort')}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-tight">
                  {language === 'fa' 
                    ? 'پاک کردن تمام عادات و تاریخچه آنها'
                    : 'Delete all habits and their history'
                  }
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 rounded p-1 border border-purple-200 dark:border-purple-700">
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-300">
                  {habits && Array.isArray(habits) && habits.length > 0 
                    ? `📊 ${habits.length} ${language === 'fa' ? 'عادت' : 'habits'}`
                    : `📊 ${language === 'fa' ? 'خالی' : 'Empty'}`
                  }
                </p>
              </div>
            </div>
            <div className="mt-2">
              <button
                onClick={() => {
                  if (confirm(t('settings.clearHabitsWarning'))) {
                    clearAllHabits()
                    alert(t('settings.clearHabitsSuccess'))
                  }
                }}
                disabled={!habits || habits.length === 0}
                className="w-full px-2 py-1 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs font-semibold rounded transition"
              >
                🗑️ {language === 'fa' ? 'حذف' : 'Clear'}
              </button>
            </div>
          </div>

          {/* Finance Card */}
          <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-md border-l-4 border-green-500 hover:shadow-lg transition overflow-hidden h-full flex flex-col">
            <div className="flex-1 space-y-1">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="text-lg">💰</span>
                  {t('settings.clearFinance')}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-tight">
                  {language === 'fa' 
                    ? 'پاک کردن تمام درآمدها و هزینه‌ها'
                    : 'Delete all incomes and expenses'
                  }
                </p>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 rounded p-1 border border-green-200 dark:border-green-700">
                <p className="text-xs font-semibold text-green-700 dark:text-green-300">
                  {items && Array.isArray(items) && items.length > 0 
                    ? `💳 ${items.length} ${language === 'fa' ? 'رکورد' : 'records'}`
                    : `💳 ${language === 'fa' ? 'خالی' : 'Empty'}`
                  }
                </p>
              </div>
            </div>
            <div className="mt-2">
              <button
                onClick={() => {
                  if (confirm(t('settings.clearFinanceWarning'))) {
                    clearAllItems()
                    alert(t('settings.clearFinanceSuccess'))
                  }
                }}
                disabled={!items || items.length === 0}
                className="w-full px-2 py-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs font-semibold rounded transition"
              >
                🗑️ {language === 'fa' ? 'حذف' : 'Clear'}
              </button>
            </div>
          </div>

          {/* Tasks Card */}
          <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-md border-l-4 border-yellow-500 hover:shadow-lg transition overflow-hidden h-full flex flex-col">
            <div className="flex-1 space-y-1">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="text-lg">📝</span>
                  {t('settings.clearTasks')}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-tight">
                  {language === 'fa' 
                    ? 'پاک کردن تمام وظایف و کارها'
                    : 'Delete all tasks and work items'
                  }
                </p>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded p-1 border border-yellow-200 dark:border-yellow-700">
                <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-300">
                  {tasks && Array.isArray(tasks) && tasks.length > 0 
                    ? `✓ ${tasks.length} ${language === 'fa' ? 'وظیفه' : 'tasks'}`
                    : `✓ ${language === 'fa' ? 'خالی' : 'Empty'}`
                  }
                </p>
              </div>
            </div>
            <div className="mt-2">
              <button
                onClick={() => {
                  if (confirm(t('settings.clearTasksWarning'))) {
                    clearAllTasks()
                    alert(t('settings.clearTasksSuccess'))
                  }
                }}
                disabled={!tasks || tasks.length === 0}
                className="w-full px-2 py-1 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs font-semibold rounded transition"
              >
                🗑️ {language === 'fa' ? 'حذف' : 'Clear'}
              </button>
            </div>
          </div>

          {/* Weekly Card */}
          <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-md border-l-4 border-blue-500 hover:shadow-lg transition overflow-hidden h-full flex flex-col">
            <div className="flex-1 space-y-1">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="text-lg">📅</span>
                  {t('settings.clearWeekly')}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-tight">
                  {language === 'fa' 
                    ? 'پاک کردن تمام برنامه‌های هفتگی'
                    : 'Delete all weekly schedules'
                  }
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-1 border border-blue-200 dark:border-blue-700">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                  {weeks && Array.isArray(weeks) && weeks.length > 0 
                    ? `📋 ${weeks.length} ${language === 'fa' ? 'هفته' : 'weeks'}`
                    : `📋 ${language === 'fa' ? 'خالی' : 'Empty'}`
                  }
                </p>
              </div>
            </div>
            <div className="mt-2">
              <button
                onClick={() => {
                  if (confirm(t('settings.clearWeeklyWarning'))) {
                    clearAllWeeks()
                    alert(t('settings.clearWeeklySuccess'))
                  }
                }}
                disabled={!weeks || weeks.length === 0}
                className="w-full px-2 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs font-semibold rounded transition"
              >
                🗑️ {language === 'fa' ? 'حذف' : 'Clear'}
              </button>
            </div>
          </div>
        </div>
        </div>

        </div>
      </div>
    </div>
  )
}